// @ts-check

import { meetsColumnRequirements } from 'utils/helpers/license';
import { countEnabledColumns } from '../countEnabledColumns';
import { getEditingSheetsPayload, createEditFingerprint } from '../selectors';
import { initialEditState } from '../state';

/** @typedef {import('../state').ExportTemplateState} ExportTemplateState */
/** @typedef {import('../state').ExportTemplateSheet} ExportTemplateSheet */
/** @typedef {import('../state').GetExportTemplateResponse} GetExportTemplateResponse */
/** @typedef {import('../state').ModalViewContext} ModalViewContext */
/** @typedef {import('../state').SaveContext} SaveContext */
/** @typedef {import('../state').SheetMetadata} SheetMetadata */
/** @typedef {import('../state').EditingSheet} EditingSheet */
/** @typedef {import('../state').EditingColumn} EditingColumn */
/** @typedef {(licenses: number[], allowForTrials?: boolean) => boolean} HasLicenseFn */

// ========== Edit State Events ==========

/**
 * User initiated saving the template (navigating to name/share settings).
 * @param {ExportTemplateState} state
 * @param {{ context: SaveContext } | undefined} payload
 * @returns {ExportTemplateState}
 */
export const templateSaveInitiated = (state, payload) => ({
  ...state,
  edit: {
    ...state.edit,
    view: 'save',
    draftSheetId: null,
    saveContext: payload?.context || 'update_existing_template',
  },
});

/**
 * User exited save view and returned to edit view.
 * @param {ExportTemplateState} state
 * @returns {ExportTemplateState}
 */
export const saveViewExited = state => ({
  ...state,
  edit: { ...state.edit, view: 'edit' },
});

/**
 * User closed the edit modal (cancel, X button, or clicked outside).
 * @param {ExportTemplateState} state
 * @returns {ExportTemplateState}
 */
export const editModalClosing = state => ({
  ...state,
  edit: { ...state.edit, view: 'closed' },
});

/**
 * The edit modal has finished closing
 * @param {ExportTemplateState} state
 * @returns {ExportTemplateState}
 */
export const editModalClosed = state => ({
  ...state,
  edit: { ...initialEditState },
});

/**
 * User initiated creation of a new template.
 *
 * When `hasLicense` is supplied, columns whose license requirements the user
 * does not meet are marked `available: false`. `useLicenseFilteredColumns`
 * hides them from the editor, `countEnabledColumns` excludes them from totals,
 * and the select-all / sheet-toggle reducers skip them so they cannot be
 * silently re-enabled into the saved payload.
 *
 * @param {ExportTemplateState} state
 * @param {{ context?: ModalViewContext, hasLicense?: HasLicenseFn } | undefined} payload
 * @returns {ExportTemplateState}
 */
export const newTemplateEditStarted = (state, payload) => {
  const [sheetMap, sheetOrder] = createSheetMapFromServerMetadata(
    state.sheetMetadata
  );

  const hasLicense = payload?.hasLicense;
  if (hasLicense) {
    for (const sheet of Object.values(sheetMap)) {
      for (const column of Object.values(sheet.columnMap)) {
        if (!meetsColumnRequirements(column.requirements, hasLicense)) {
          column.available = false;
          // New template: the user has never selected this column, so it must
          // not ship in the save payload. (Existing templates preserve the
          // server's `enabled` value so a licence re-upgrade restores prior
          // selections — that path runs through existingTemplateEditStarted.)
          column.enabled = false;
        }
      }
      sheet.selectedColumns = countEnabledColumns(sheet);
      sheet.totalColumns = countEnabledColumns(sheet);
    }
  }

  return {
    ...state,
    edit: {
      ...initialEditState,
      view: 'edit',
      context: payload?.context || 'template_management',
      saveContext: 'new_template',
      sheetMap,
      sheetOrder,
      snapshot: createEditFingerprint(sheetMap, sheetOrder),
    },
  };
};

/**
 * User started editing an existing template.
 *
 * Reconciliation rules when the server-side metadata has evolved since the
 * template was saved:
 * - Orphan SHEET (referenced by template, missing from metadata): dropped.
 *   Sheet reinstatement is not supported.
 * - Orphan COLUMN inside a known sheet: hidden from the editor but
 *   round-tripped via `orphanedColumnIds` so reinstatement preserves user state.
 *
 * When `hasLicense` is supplied, columns the user no longer has the licence
 * for are marked `available: false`. `useLicenseFilteredColumns` hides them
 * from the editor unconditionally; `getEditingSheetsPayload` keeps them in
 * the save payload (so a later licence re-upgrade restores the user's prior
 * selections), and `enabled` is intentionally not mutated.
 *
 * @param {ExportTemplateState} state
 * @param {{response: GetExportTemplateResponse, context: ModalViewContext | undefined, hasLicense?: HasLicenseFn}} payload
 * @returns {ExportTemplateState}
 */
export const existingTemplateEditStarted = (state, payload) => {
  const [sheetMap, metadataSheetOrder] = createSheetMapFromServerMetadata(
    state.sheetMetadata
  );

  // On the server, the _omission_ of data indicates disabled
  // so set everything to disabled before iterating over the
  // server sheets.
  for (const clientSheet of Object.values(sheetMap)) {
    clientSheet.enabled = false;
    for (const clientColumn of Object.values(clientSheet.columnMap)) {
      clientColumn.enabled = false;
    }
  }

  /** @type {Set<string>} */
  const knownServerSheetIds = new Set();

  for (const serverSheet of payload.response.sheets) {
    const clientSheet = sheetMap[serverSheet.sheetMetadataId];
    if (!clientSheet) continue;

    knownServerSheetIds.add(serverSheet.sheetMetadataId);
    clientSheet.overridenName = serverSheet.overridenName || null;
    clientSheet.enabled = true;

    /** @type {string[]} */
    const newColumnOrder = [];
    /** @type {string[]} */
    const orphanedColumnIds = [];

    for (const serverColumn of serverSheet.columns) {
      const clientColumn = clientSheet.columnMap[serverColumn.columnMetadataId];
      if (clientColumn) {
        clientColumn.enabled = true;
        clientColumn.overridenName = serverColumn.overridenName || null;
        newColumnOrder.push(serverColumn.columnMetadataId);
      } else {
        orphanedColumnIds.push(serverColumn.columnMetadataId);
        clientSheet.columnMap[serverColumn.columnMetadataId] = {
          columnMetadataId: serverColumn.columnMetadataId,
          overridenName: serverColumn.overridenName || null,
          defaultName: '',
          enabled: true,
          mandatory: false,
          requirements: [],
          orphaned: true,
        };
      }
    }

    // Append metadata-only columns (added server-side after template was saved).
    const seenIds = new Set(newColumnOrder);
    for (const metadataColumnId of clientSheet.columnOrder) {
      if (!seenIds.has(metadataColumnId)) newColumnOrder.push(metadataColumnId);
    }
    clientSheet.columnOrder = newColumnOrder;
    clientSheet.orphanedColumnIds = orphanedColumnIds;
  }

  // Sheet order: server sheets in their order (skipping orphan sheets),
  // then metadata-only sheets added after the template was saved.
  const sheetOrder = [];
  for (const serverSheet of payload.response.sheets) {
    if (knownServerSheetIds.has(serverSheet.sheetMetadataId)) {
      sheetOrder.push(serverSheet.sheetMetadataId);
    }
  }
  for (const metadataSheetId of metadataSheetOrder) {
    if (!knownServerSheetIds.has(metadataSheetId)) {
      sheetOrder.push(metadataSheetId);
    }
  }

  // Mark licence-blocked columns unavailable. `enabled` is left untouched so
  // a downgrade-then-re-upgrade round trip restores the user's prior column
  // selections. The select-all / sheet-toggle reducers skip unavailable
  // columns; the editor hides them entirely.
  const context = payload.context || 'template_management';
  const applyAvailability = !!payload.hasLicense;
  if (applyAvailability) {
    for (const clientSheet of Object.values(sheetMap)) {
      for (const clientColumn of Object.values(clientSheet.columnMap)) {
        if (clientColumn.orphaned) continue;
        if (
          !meetsColumnRequirements(
            clientColumn.requirements,
            payload.hasLicense
          )
        ) {
          clientColumn.available = false;
        }
      }
    }
  }

  // Rebuild column counts to reflect only available, non-orphan columns.
  // countEnabledColumns honours `available`, so selectedColumns reflects
  // (enabled AND available).
  for (const clientSheet of Object.values(sheetMap)) {
    clientSheet.selectedColumns = countEnabledColumns(clientSheet);
    let availableTotal = 0;
    for (const col of Object.values(clientSheet.columnMap)) {
      if (col.orphaned) continue;
      if (col.available === false) continue;
      availableTotal++;
    }
    clientSheet.totalColumns = availableTotal;
  }

  // Is this user the owner of the template being edited?
  const sourceTemplateIsOwner =
    payload.response.sharedFromTemplateId === undefined ||
    payload.response.sharedFromTemplateId === null;

  return {
    ...state,
    edit: {
      ...initialEditState,
      view: 'edit',
      context,
      sheetMap,
      sheetOrder,
      snapshot: createEditFingerprint(sheetMap, sheetOrder),
      sourceTemplateName: payload.response.name,
      sourceTemplateId: payload.response.id,
      sourceSharedWithIds: (payload.response.sharedWith || []).map(sw => sw.id),
      sourceTemplateIsOwner,
    },
  };
};

/**
 * A sheet was toggled on/off in the editing template.
 * @param {ExportTemplateState} state
 * @param {{ sheetId: string, included: boolean }} payload
 * @returns {ExportTemplateState}
 */
export const editingSheetToggled = (state, payload) => {
  const sheet = state.edit.sheetMap[payload.sheetId];
  const toggled = { ...sheet, enabled: payload.included };

  // When re-enabling a sheet that has zero enabled columns,
  // select all columns so the user doesn't land on "0 of N".
  // Unavailable columns are not touched: they are hidden from the user, so
  // mutating them here would silently overwrite persisted intent the user
  // has no way to see.
  if (payload.included && countEnabledColumns(toggled) === 0) {
    /** @type Record<string, EditingColumn> */
    const columnMap = {};
    for (const [id, col] of Object.entries(toggled.columnMap)) {
      columnMap[id] = col.available === false ? col : { ...col, enabled: true };
    }
    toggled.columnMap = columnMap;
  }

  toggled.selectedColumns = countEnabledColumns(toggled);
  return {
    ...state,
    edit: {
      ...state.edit,
      sheetMap: { ...state.edit.sheetMap, [payload.sheetId]: toggled },
    },
  };
};

/**
 * A specific sheet's properties (columns, overridenName) were updated.
 * @param {ExportTemplateState} state
 * @param {{ sheetId: string, overridenName: string | null }} payload
 * @returns {ExportTemplateState}
 */
export const editingSheetNameChanged = (state, payload) => ({
  ...state,
  edit: {
    ...state.edit,
    sheetMap: {
      ...state.edit.sheetMap,
      [payload.sheetId]: {
        ...state.edit.sheetMap[payload.sheetId],
        overridenName: payload.overridenName,
      },
    },
  },
});

/**
 * Sheet order was changed.
 * @param {ExportTemplateState} state
 * @param {string[]} payload
 * @returns {ExportTemplateState}
 */
export const sheetOrderChanged = (state, payload) => ({
  ...state,
  edit: { ...state.edit, sheetOrder: payload },
});

/**
 * Column order was changed within a sheet.
 * Operates on draftSheet when in column editing mode.
 * @param {ExportTemplateState} state
 * @param {{ sheetId: string, newOrder: string[] }} payload
 * @returns {ExportTemplateState}
 */
export const editingSheetColumnOrderUpdated = (state, payload) => {
  const { draftSheet } = state.edit;

  if (draftSheet) {
    return {
      ...state,
      edit: {
        ...state.edit,
        draftSheet: { ...draftSheet, columnOrder: payload.newOrder },
      },
    };
  }

  const sheet = state.edit.sheetMap[payload.sheetId];
  return {
    ...state,
    edit: {
      ...state.edit,
      sheetMap: {
        ...state.edit.sheetMap,
        [payload.sheetId]: { ...sheet, columnOrder: payload.newOrder },
      },
    },
  };
};

/**
 * Settings modal was opened for an existing template.
 * @param {ExportTemplateState} state
 * @param {GetExportTemplateResponse} payload
 * @returns {ExportTemplateState}
 */
export const settingsModalOpened = (state, payload) => ({
  ...state,
  edit: {
    ...state.edit,
    view: 'settings',
    sourceTemplateId: payload.id,
    sourceTemplateName: payload.name,
    sourceSharedWithIds: (payload.sharedWith || []).map(u => u.id),
  },
});

// ========== One-Time Export Events ==========

/**
 * User triggered a one-time export from the template editor.
 * @param {ExportTemplateState} state
 * @returns {ExportTemplateState}
 */
export const oneTimeExportTriggered = state => ({
  ...state,
  edit: { ...initialEditState },
  oneTimeExport: getEditingSheetsPayload({ exportTemplate: state }),
});

/**
 * The one-time export has been consumed by the Summary component.
 * @param {ExportTemplateState} state
 * @returns {ExportTemplateState}
 */
export const oneTimeExportConsumed = state => ({
  ...state,
  oneTimeExport: null,
});

// ========== Helper Logic ==========

/**
 * @param {SheetMetadata[]} sheetMetadataArray
 * @returns {[Record<string, EditingSheet>, string[]]} Tuple of the new map and the sheet order
 */
const createSheetMapFromServerMetadata = sheetMetadataArray => {
  /** @type {Record<string, EditingSheet>} */
  const sheetMap = {};
  const sheetOrder = [];

  for (const sheetMd of sheetMetadataArray) {
    /** @type {Record<string, EditingColumn>} */
    const columnMap = {};
    const columnOrder = [];
    let anyColumnMandatory = false;

    for (const columnMd of sheetMd.columns) {
      const mandatory = columnMd.mandatory;
      anyColumnMandatory = anyColumnMandatory || mandatory;
      columnMap[columnMd.id] = {
        columnMetadataId: columnMd.id,
        overridenName: null,
        defaultName: columnMd.name,
        enabled: true,
        mandatory,
        requirements: columnMd.requirements || [],
      };
      columnOrder.push(columnMd.id);
    }

    sheetMap[sheetMd.id] = {
      sheetMetadataId: sheetMd.id,
      enabled: true,
      overridenName: null,
      columnMap,
      columnOrder,
      mandatory: anyColumnMandatory,
      defaultName: sheetMd.name,
      selectedColumns: columnOrder.length,
      totalColumns: columnOrder.length,
      orphanedColumnIds: [],
    };
    sheetOrder.push(sheetMd.id);
  }

  return [sheetMap, sheetOrder];
};
